/**
 * Keeps the "Select Frequency" widget prices in sync with the selected
 * quantity tier. Verified against the store's discount engine: the
 * "Buy 2+/4+" automatic discounts stack on top of the selling-plan
 * (subscription) price, so both rows always show the tier-discounted
 * per-unit price, e.g. "$80.10 each".
 *
 * 2026-10-14 additions (subscribe-first card, [data-subs-card]):
 *  - Plain "$80.10" prices in the card (no "each" suffix).
 *  - "[data-sub-saving]" (per-bottle saving vs buying once) follows the
 *    tier, the plan and the free-shipping threshold.
 *  - Shipping copy, bottle counts and the quantity tiles follow the
 *    selected purchase type and quantity.
 *  - Afterpay instalment follows the selected option; the struck-out
 *    compare price in the button hides when the total is not discounted.
 *  - Runs on products without quantity tiles too.
 */
(function () {
  'use strict';

  var QTY_BUTTON = '[data-quantity-button]';
  var FREE_SHIPPING_THRESHOLD = 9900;

  function formatMoney(cents) {
    var currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return '$' + (cents / 100).toFixed(2) + ' ' + currency;
  }

  function formatPlain(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function init(container) {
    var widget = container.querySelector('[data-product-subscription-options]');
    if (!widget) return;

    var hasTiers = !!container.querySelector(QTY_BUTTON);
    var isCard = widget.hasAttribute('data-subs-card');
    if (!hasTiers && !isCard) return;

    var shipSavingEl = widget.querySelector('[data-ship-saving]');
    var onetimeUnitsEl = widget.querySelector('[data-onetime-units]');
    var onetimeShippingEl = widget.querySelector('[data-onetime-shipping]');
    var subPctEl = widget.querySelector('[data-sub-pct]');
    var savingEl = widget.querySelector('[data-sub-saving]');
    var shippingSaving = savingEl
      ? Number(savingEl.getAttribute('data-shipping-saving')) || 0
      : 0;
    var afterpayPrice = container.querySelector('[data-afterpay-price]');

    /**
     * Raw variant price in cents. The tiles' data-variant-price can't be
     * used: the theme rebases it on the checked purchase-type radio, so it
     * already includes the subscription discount when autodeliver is on.
     */
    var onetimeRadio = widget.querySelector('[data-recharge-checkbox][value="onetime"]');
    var variantPrice = onetimeRadio
      ? Number(onetimeRadio.getAttribute('data-recharge-price'))
      : 0;

    /**
     * Pricing block inside the add-to-bag button. The bundle replaces its
     * innerHTML wholesale (new spans without data attributes), so cache the
     * container and re-query the price span on every pass.
     */
    var addBtnPricing = container.querySelector(
      '[data-product-form-add-main] [data-product-pricing]'
    );

    /**
     * Only writes when the text differs, so the MutationObserver below can
     * call apply() without looping.
     */
    function writeAll(selector, cents) {
      widget.querySelectorAll(selector).forEach(function (el) {
        var text = isCard ? formatPlain(cents) : formatMoney(cents) + ' each';
        if (el.innerText.trim() !== text) el.innerText = text;
      });
    }

    function apply() {
      if (!variantPrice) return;

      var selected = container.querySelector(QTY_BUTTON + '.is-selected');
      if (hasTiers && !selected) return;

      var tierPct = selected ? Number(selected.getAttribute('data-discount-value')) || 0 : 0;
      var qty = selected ? Number(selected.getAttribute('value')) || 1 : 1;

      var planSelect = widget.querySelector('[data-product-recharge-select]');
      var discountEl = widget.querySelector('[data-recharge-discount]');
      var subPct = discountEl
        ? Number(discountEl.getAttribute('data-recharge-discount')) || 0
        : 0;
      if (planSelect && planSelect.options && planSelect.selectedIndex >= 0) {
        var selectedPlan = planSelect.options[planSelect.selectedIndex];
        var planPct = selectedPlan.getAttribute('data-discount-percent');
        if (planPct != null && planPct !== '') {
          subPct = Number(planPct) / 100;
          if (discountEl) {
            discountEl.setAttribute('data-recharge-discount', String(subPct));
            if (!isCard) {
              var badge = Math.round(subPct * 100) + '% OFF';
              if (discountEl.innerText.trim() !== badge) discountEl.innerText = badge;
            }
          }
          if (subPctEl) {
            var pctText = String(Math.round(subPct * 100));
            if (subPctEl.innerText.trim() !== pctText) subPctEl.innerText = pctText;
          }
          var planPrice = selectedPlan.getAttribute('data-plan-price');
          var autodeliverRadio = widget.querySelector(
            '[data-recharge-checkbox][value="autodeliver"]'
          );
          if (planPrice && autodeliverRadio) {
            autodeliverRadio.setAttribute('data-recharge-price', planPrice);
          }
        }
      }

      var onetime = Math.round(variantPrice * (1 - tierPct / 100));
      var subscribe = Math.round(variantPrice * (1 - subPct) * (1 - tierPct / 100));

      writeAll('[data-onetime-price]', onetime);
      writeAll('[data-discount-price]', subscribe);

      var autodeliver = widget.querySelector('[data-recharge-checkbox][value="autodeliver"]');
      var isSub = !!(autodeliver && autodeliver.checked);
      var perUnit = isSub ? subscribe : onetime;

      /* One-time orders ship free from the threshold, so the shipping
         component of the saving (and the "+ $9.95 shipping" note) only
         hold while the one-time line total is under it. */
      var oneTimeShipsFree = onetime * qty >= FREE_SHIPPING_THRESHOLD;
      if (shipSavingEl) shipSavingEl.hidden = oneTimeShipsFree || !shippingSaving;
      if (onetimeShippingEl) {
        var shipText = oneTimeShipsFree || !shippingSaving ? 'free shipping' : '+ $9.95 shipping';
        if (onetimeShippingEl.innerText.trim() !== shipText) onetimeShippingEl.innerText = shipText;
      }
      if (onetimeUnitsEl) {
        var unitsText = qty + (qty === 1 ? ' bottle' : ' bottles');
        if (onetimeUnitsEl.innerText.trim() !== unitsText) onetimeUnitsEl.innerText = unitsText;
      }
      if (savingEl) {
        var shipSaved = oneTimeShipsFree ? 0 : shippingSaving;
        var savingText = formatPlain(onetime - subscribe + shipSaved);
        if (savingEl.innerText.trim() !== savingText) savingEl.innerText = savingText;
      }

      /* Card layout: the quantity tiles sit under the button as "Stocking
         up?". Per-bottle price follows the selected purchase type and the
         labels say bottles. Not observed (see below). */
      if (isCard) {
        container.querySelectorAll(QTY_BUTTON).forEach(function (tile) {
          var tilePct = Number(tile.getAttribute('data-discount-value')) || 0;
          var tileQty = Number(tile.getAttribute('value')) || 1;
          var base = isSub ? variantPrice * (1 - subPct) : variantPrice;
          var unit = Math.round(base * (1 - tilePct / 100));
          var priceEl = tile.querySelector('.o-quantity-selector__qty-btn-price');
          if (priceEl) {
            var tileText = formatPlain(unit) + ' each';
            if (priceEl.innerText.trim() !== tileText) priceEl.innerText = tileText;
          }
          var labelEl = tile.querySelector('.o-quantity-selector__qty-btn-value');
          if (labelEl) {
            var labelText = tileQty + (tileQty === 1 ? ' bottle' : ' bottles');
            if (labelEl.innerText.trim() !== labelText) labelEl.innerText = labelText;
          }
          var unitEl = tile.querySelector('.o-quantity-selector__qty-btn-unit');
          if (unitEl && tilePct) {
            var unitText = '· save ' + tilePct + '%';
            if (unitEl.innerText.trim() !== unitText) unitEl.innerText = unitText;
          }
        });

        /* "Just 1 bottle" link: the 1-bottle tile is hidden by CSS on
           subscribe-first products, so this is how a shopper gets back to
           one once she has picked 2 or 4. Shows the current per-bottle price. */
        var resetEl = container.querySelector('[data-qty-reset]');
        if (resetEl) {
          var showReset = qty > 1;
          if (resetEl.hidden === showReset) resetEl.hidden = !showReset;
          var resetPriceEl = resetEl.querySelector('[data-qty-reset-price]');
          if (resetPriceEl) {
            var singleUnit = Math.round(isSub ? variantPrice * (1 - subPct) : variantPrice);
            var resetText = formatPlain(singleUnit);
            if (resetPriceEl.innerText.trim() !== resetText) resetPriceEl.innerText = resetText;
          }
        }
      }

      /**
       * The bundle discounts the line total (75.65 x 2 x 0.9 = 136.17), but
       * checkout rounds the per-unit price first (75.65 x 0.9 = 68.085 ->
       * 68.09 -> 136.18), so mirror checkout: rounded per-unit x quantity.
       */
      var addBtnMoney = addBtnPricing && addBtnPricing.querySelector('.o-pricing__price');
      if (addBtnMoney && hasTiers) {
        var totalText = formatPlain(perUnit * qty);
        if (addBtnMoney.innerText.trim() !== totalText) {
          addBtnMoney.innerText = totalText;
        }
      }

      /* Only show the struck-out compare price when there is something to
         strike through (live showed "$89.00 $89.00" on a one-time single). */
      var addBtnCompare = addBtnPricing && addBtnPricing.querySelector('.o-pricing__compare');
      if (addBtnCompare && isCard) {
        var sameAsTotal = addBtnCompare.innerText.trim() === formatPlain(perUnit * qty);
        var wanted = sameAsTotal ? 'none' : '';
        if (addBtnCompare.style.display !== wanted) addBtnCompare.style.display = wanted;
      }

      /* Afterpay: 4 instalments of the amount actually being added. */
      if (afterpayPrice && isCard) {
        var instalment = Math.round((perUnit * qty) / 4);
        var apText = formatPlain(instalment);
        if (afterpayPrice.innerText.trim() !== apText) {
          afterpayPrice.innerText = apText;
          afterpayPrice.setAttribute('data-money', String(instalment));
        }
      }
    }

    /**
     * The theme bundle rewrites the same price nodes (with single-unit
     * values) inside its own handlers for these events, so defer a tick
     * to run after it.
     */
    function applyLater() {
      window.setTimeout(apply, 0);
    }

    container.addEventListener('onQtyChange', applyLater);
    container.addEventListener('onVariantChange', function (event) {
      var newVariant = event.detail && event.detail.newVariant;
      if (newVariant && newVariant.price) variantPrice = Number(newVariant.price);
      applyLater();
    });
    container.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      if (event.target.closest('[data-qty-reset]')) {
        event.preventDefault();
        var firstTile = container.querySelector(QTY_BUTTON + '[value="1"]');
        if (firstTile) firstTile.click();
        return;
      }
      if (event.target.closest(QTY_BUTTON)) applyLater();
    });
    /* Switching between one-time and subscribe, or the plan, changes the total. */
    container.addEventListener('change', function (event) {
      if (
        event.target.matches &&
        event.target.matches('[data-recharge-checkbox], [data-product-recharge-select]')
      ) {
        applyLater();
      }
    });

    /**
     * The theme bundle also rewrites these nodes outside the events above
     * (e.g. during its own late init on page load), which would strip the
     * "each" suffix. Re-apply whenever anyone else touches them; writeAll's
     * same-text guard keeps this from looping. The quantity tiles are
     * deliberately NOT observed: the bundle rewrites them on its own passes
     * and observing them ping-pongs until the tab freezes.
     */
    var observer = new MutationObserver(apply);
    widget
      .querySelectorAll('[data-onetime-price], [data-discount-price]')
      .forEach(function (el) {
        observer.observe(el, { childList: true, characterData: true, subtree: true });
      });
    if (addBtnPricing) {
      observer.observe(addBtnPricing, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
    if (afterpayPrice && isCard) {
      observer.observe(afterpayPrice, { childList: true, characterData: true, subtree: true });
    }

    apply();
  }

  function boot() {
    document.querySelectorAll('[data-product-main]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
